import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { ClipboardList, Link as LinkIcon, Pencil, Plus } from 'lucide-react';
import { BACKEND_URL as API } from '../../apiConfig';
import { Dialog, DialogContent, DialogTitle } from '../ui/dialog';
import './vehicleMaintenance.css';

const labels = {
  ja: { title: '点検記録', add: '整備を記録', link: '記録リンクをコピー', copied: 'コピーしました', all: 'すべての車両', vehicle: '車両', date: '実施日', save: '保存', saving: '保存中', empty: '記録はありません', more: 'さらに表示', author: '記録者', close: '閉じる', choose: '車両を選択', next: '次回点検', items: { inspection: '3か月点検完了', vehicle_inspection: '車検', summer_tires: '夏タイヤ交換', winter_tires: '冬タイヤ交換', engine_oil: 'エンジンオイル交換', oil_filter: 'オイルフィルター交換', other: 'その他' } },
};
const today = () => new Intl.DateTimeFormat('sv-SE', { timeZone: 'Asia/Tokyo' }).format(new Date());
const blank = () => ({ request_id: crypto.randomUUID(), vehicle_id: '', performed_on: today(), items: [], other: '', submitter_name: '' });
const errorText = (error) => {
  if (error.response?.status === 401) return 'ログインの有効期限が切れました。再度ログインしてください。';
  if (error.response?.status === 403) return 'この操作を行う権限がありません。管理者にお問い合わせください。';
  const detail = error.response?.data?.detail;
  return typeof detail === 'string' && /[ぁ-んァ-ン]/.test(detail) ? detail : '処理に失敗しました。通信状況を確認して再度お試しください。';
};

function MaintenanceDialog({ publicMode, title, saving, onClose, children }) {
  if (publicMode) return children;
  return <Dialog open onOpenChange={value => { if (!value && !saving) onClose(); }}>
    <DialogContent className="internal-page-skin fleet-premium max-w-lg max-h-[90dvh] overflow-y-auto bg-[#0c0d0e] p-5" aria-describedby={undefined}>
      <DialogTitle>{title}</DialogTitle>{children}
    </DialogContent>
  </Dialog>;
}

export default function VehicleMaintenance({ vehicles, canEdit, onSaved, client = axios, endpoint = `${API}/api/internal/vehicle-maintenance`, driverMode = false, publicMode = false, initialVehicleId = '', onOpenHistory }) {
  const t = labels.ja;
  const [open, setOpen] = useState(() => driverMode || new URLSearchParams(window.location.search).get('maintenance') === '1');
  const [form, setForm] = useState(blank);
  const [editing, setEditing] = useState(null);
  const canEditHistory = canEdit && !driverMode && !publicMode;
  const [records, setRecords] = useState([]);
  const [filter, setFilter] = useState(initialVehicleId);
  const [more, setMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');

  useEffect(() => {
    if (publicMode) return undefined;
    let active = true;
    setRecords([]); setMore(false); setError('');
    setLoading(true);
    client.get(endpoint, { params: { vehicle_id: filter } })
      .then(({ data }) => { if (active) { setRecords(data); setMore(data.length === 100); } })
      .catch(e => { if (active) setError(errorText(e)); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [filter, client, endpoint, publicMode, vehicles]);

  const loadMore = async () => {
    setLoading(true);
    try {
      const { data } = await client.get(endpoint, { params: { vehicle_id: filter, skip: records.length } });
      setRecords(current => [...current, ...data]); setMore(data.length === 100);
    } catch (e) { setError(errorText(e)); }
    finally { setLoading(false); }
  };
  const toggle = (key) => setForm(current => {
    let items = current.items.includes(key) ? current.items.filter(item => item !== key) : [...current.items, key];
    if (key === 'summer_tires') items = items.filter(item => item !== 'winter_tires');
    if (key === 'winter_tires') items = items.filter(item => item !== 'summer_tires');
    return { ...current, items };
  });
  const save = async e => {
    e.preventDefault(); if (saving) return;
    setSaving(true); setError('');
    try {
      if (editing) await client.put(`${endpoint}/${editing.id}`, { ...form, revision: editing.revision || 0 });
      else await client.post(endpoint, form);
      if (publicMode) {
        setMessage('整備記録を保存しました。'); setForm(blank()); onSaved(); return;
      }
      const { data } = await client.get(endpoint, { params: { vehicle_id: filter } });
      setRecords(data); setMore(data.length === 100); setOpen(false); setEditing(null); setForm(blank()); onSaved?.();
    } catch (e) { setError(errorText(e)); }
    finally { setSaving(false); }
  };
  const copy = async () => {
    const url = new URL('/driver/maintenance', window.location.origin);
    try { await navigator.clipboard.writeText(url.href); setMessage(t.copied); }
    catch { setMessage(url.href); }
  };
  const input = 'w-full min-w-0 rounded border border-slate-600 bg-slate-900 p-2 text-white';
  const selected = vehicles.find(v => v.id === form.vehicle_id);
  return <section className="fleet-maintenance-section border-y border-slate-700 py-5 space-y-4">
    <div className="fleet-maintenance-toolbar gap-3">
      {onOpenHistory ? <button type="button" onClick={onOpenHistory} className="text-lg font-semibold inline-flex items-center gap-2 text-cyan-300"><ClipboardList size={20} />すべての点検・オイル交換履歴</button> : <h2 className="text-lg font-semibold flex items-center gap-2"><ClipboardList size={20} />{t.title}</h2>}
      {!publicMode && !initialVehicleId && <select aria-label={t.vehicle} disabled={loading} value={filter} onChange={e => setFilter(e.target.value)} className="max-w-full rounded bg-slate-800 p-2">
        <option value="">{t.all}</option>{vehicles.map(v => <option key={v.id} value={v.id}>{v.plate_number}</option>)}
      </select>}
      {canEdit && !publicMode && <><button type="button" onClick={() => { setError(''); setEditing(null); setForm(blank()); setOpen(true); }} className="inline-flex items-center gap-2 rounded border border-cyan-600 px-3 py-2"><Plus size={16} />{t.add}</button>
        <button type="button" title={t.link} aria-label={t.link} onClick={copy} className="p-2"><LinkIcon size={18} /></button></>}
    </div>
    {message && <p role="status" className="text-sm break-all">{message}</p>}
    {error && <p role="alert" className="text-red-400">{error}</p>}
    {loading && <p role="status">読み込み中...</p>}
    {!publicMode && !loading && records.length === 0 && <p className="text-slate-400">{t.empty}</p>}
    {!publicMode && records.length > 0 && <div className="fleet-single-line-scroll" tabIndex={0} aria-label="整備履歴一覧">
      <table className="fleet-single-line-table text-left">
        <thead><tr><th>実施日</th><th>車両</th><th>整備内容</th><th>記録者</th><th>最終更新</th>{canEditHistory && <th>操作</th>}</tr></thead>
        <tbody className="divide-y divide-slate-700">{records.map(r => <tr key={r.id}>
          <td><time>{r.performed_on}</time></td><td>{r.plate_number}</td>
          <td>{r.items.map(key => t.items[key] || key).join(' / ')}{r.other ? `: ${r.other}` : ''}</td>
          <td>{r.created_by_name}{r.source === 'public_link' ? '（公開フォーム・本人未確認）' : ''}</td>
          <td>{r.updated_at ? `${r.updated_by_name} · ${new Date(r.updated_at).toLocaleString('ja-JP', { timeZone: 'Asia/Tokyo' })}` : '—'}</td>
          {canEditHistory && <td><button type="button" title="記録を編集" aria-label={`${r.performed_on} ${r.plate_number} 記録を編集`} onClick={() => { setEditing(r); setForm({ ...blank(), request_id: r.id, vehicle_id: r.vehicle_id, performed_on: r.performed_on, items: r.items, other: r.other || '' }); setError(''); setOpen(true); }} className="inline-flex items-center gap-2"><Pencil size={16} />編集</button></td>}
        </tr>)}</tbody>
      </table>
    </div>}
    {more && <button type="button" disabled={loading} onClick={loadMore}>{t.more}</button>}
    {(open || publicMode) && canEdit && <MaintenanceDialog publicMode={publicMode} title={editing ? '整備記録を編集' : t.add} saving={saving} onClose={() => setOpen(false)}>
      <form aria-label={editing ? '整備記録を編集' : t.add} onSubmit={save} className="fleet-maintenance-form w-full min-w-0 space-y-4">
        {publicMode && <label className="block">記録者名<input required maxLength={100} autoComplete="name" className={input} value={form.submitter_name} onChange={e => setForm({ ...form, submitter_name: e.target.value })} /></label>}
        <label className="block">{t.vehicle}<select required className={input} value={form.vehicle_id} onChange={e => setForm({ ...form, vehicle_id: e.target.value })}>
          <option value="">{t.choose}</option>{vehicles.filter(v => v.status !== 'retired' || (editing && v.id === editing.vehicle_id)).map(v => <option key={v.id} value={v.id}>{v.plate_number}</option>)}
        </select></label>
        <label className="block">{t.date}<input required type="date" max={today()} className={input} value={form.performed_on} onChange={e => setForm({ ...form, performed_on: e.target.value })} /></label>
        <fieldset disabled={saving} className="fleet-maintenance-items"><legend>整備項目</legend>{Object.entries(t.items).map(([key, label]) => <label key={key} className="flex items-center gap-3"><input type="checkbox" checked={form.items.includes(key)} onChange={() => toggle(key)} />{label}</label>)}</fieldset>
        {form.items.includes('other') && <textarea required maxLength={1000} aria-label={t.items.other} className={input} value={form.other} onChange={e => setForm({ ...form, other: e.target.value })} />}
        {selected?.maintenance_next_date && <p>{t.next}: {selected.maintenance_next_date}</p>}
        {error && <p role="alert" className="text-red-400">{error}</p>}
        <button disabled={saving || !form.items.length} className="w-full rounded bg-cyan-700 p-3 disabled:opacity-50">{saving ? t.saving : t.save}</button>
      </form>
    </MaintenanceDialog>}
  </section>;
}
